import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Download } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { fadeInUp, staggerContainer, viewportConfig } from "@/lib/animations";
import { useRef } from "react";

export const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const patternY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const patternScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const patternOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.05, 0.03, 0.01]);

  return (
    <section 
      ref={sectionRef}
      className="relative min-h-[90vh] flex items-center bg-primary text-primary-foreground overflow-hidden"
    >
      {/* Parallax background pattern */}
      <motion.div 
        className="absolute inset-0"
        style={{ 
          y: patternY,
          scale: patternScale,
          opacity: patternOpacity,
        }}
      >
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </motion.div>

      <motion.div
        className="section-container relative z-10 py-20"
        initial="hidden"
        whileInView="visible"
        viewport={viewportConfig}
        variants={staggerContainer}
      >
        <div className="max-w-4xl">
          {/* Eyebrow - no animation to improve LCP */}
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground/50 mb-8">
            Strategic Operations & AI Advisory for US SMBs
          </p>

          {/* Headline - no animation to improve LCP */}
          <h1 className="heading-1 mb-8">
            The Future of Business Will Not Be Led by Giants Alone.
            <span className="block mt-2 text-primary-foreground/80">
              SMBs Will Shape the Next Economy.
            </span>
          </h1>

          {/* Subheadline - no animation to improve LCP */}
          <p className="body-large text-primary-foreground/70 max-w-3xl mb-12">
            Inductus International partners with SMBs, family businesses, and early-growth companies 
            across the United States to audit, restructure, and future-proof operations, finance, 
            and strategy in the age of AI — without losing control, culture, or clarity.
          </p>

          {/* CTAs */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            variants={fadeInUp}
          >
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                asChild
                className="rounded-none px-8 py-6 bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-sans text-sm font-medium uppercase tracking-wide"
              >
                <Link to="/request-assessment" className="flex items-center gap-2">
                  Request a Strategic Assessment
                  <ArrowRight size={16} />
                </Link>
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.98 }}>
              <Button
                asChild
                variant="outline"
                className="rounded-none px-8 py-6 bg-primary-foreground/5 border-2 border-primary-foreground/50 text-primary-foreground hover:bg-primary-foreground/15 hover:border-primary-foreground/70 animate-pulse-glow transition-all duration-300 font-sans text-sm font-medium uppercase tracking-wide"
              >
                <Link to="/ai-readiness-checklist" className="flex items-center gap-2">
                  <Download size={16} />
                  Download AI Readiness Checklist
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
